import { api } from "services/axios/api";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";
import { IComponente, IComponenteCor, ISelectOption } from "../types/componente.types";
import { RetornoMidia, RetornoNomeMidia, RetornoRemocaoMidia } from "../../types/midia.types";

// ===== CRUD de Componente =====

export const obterComponentePorId = async (idComponente: number): Promise<IComponente | null> => {
  try {
    const { data } = await api.get(`/ComponenteN1/ObterPorId/${idComponente}`);
    
    if (!data) return null;
    
    return {
      idComponente: data.idComponente,
      codigo: data.codigo || "",
      codigoInterno: data.codigoInterno,
      descricao: data.descricao || "",
      idSituacao: data.idSituacao || 1,
      idStatus: data.idStatus || 1,
      idReferencia: data.idReferencia,
      idLinha: data.idLinha,
      idGrupo: data.idGrupo,
      // FASE 05 — 2ª validação/PO 2026-07-24 (D9): o formulário trata sempre como Cor única
      // (Colorido é backlog futuro), inclusive ao consultar cadastros antigos.
      aplicacao: "cor_unica",
      idCor: data.idCor,
      corDescricao: data.cor,
      idUnidadeItem: data.idUnidadeItem || 0,
      idUnidadeEmbalagem: data.idUnidadeItemEmbalagem || 0,
      quantidadeEmbalagem: data.quantidadeEmbalagem || 1,
      // 02/09 (A10a): a coluna passou a existir e persistir; ?? preserva o valor real (|| virava 1)
      loteMinimoQtdEmbalagens: data.loteMinimoQtdEmbalagens ?? 1,
      otimizacaoBarra: data.otimizacaoBarra,
      // Mapear campos de mídia
      imagemUrl: data.imagem || undefined,
      imagemNomeArquivo: data.imagemNomeArquivo || undefined,
      // FASE 05 — 2ª validação/PO 2026-07-24 (D2): ícone e ficha técnica.
      iconeUrl: data.icone || undefined,
      iconeNomeArquivo: data.iconeNomeArquivo || undefined,
      fichaTecnicaUrl: data.fichaTecnica || undefined,
      fichaTecnicaNomeArquivo: data.fichaTecnicaNomeArquivo || undefined,
      // Cores já vêm do backend quando aplicacao = 'colorido'
      cores: data.cores || [],
      // Campos de auditoria
      dataUltimaAtualizacao: data.dataAlteracao,
      dataCriacao: data.dataCriacao,
      editavel: data.editavel,
      excluido: data.excluido,
    };
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao obter componente");
    return null;
  }
};

export const salvarComponente = async (componente: Partial<IComponente>): Promise<{ id: number; sucesso: boolean; mensagem: string } | null> => {
  try {
    const isEdicao = !!componente.idComponente;
    const endpoint = isEdicao ? `/ComponenteN1/Atualizar/${componente.idComponente}` : '/ComponenteN1/Criar';
    const method = isEdicao ? 'put' : 'post';
    
    // Limpar valores inválidos antes de enviar
    const dadosLimpos: any = { ...componente };
    
    // Remover campos de mídia (URLs) do payload - apenas nomes de arquivo são enviados
    delete dadosLimpos.imagemUrl;
    delete dadosLimpos.imagemNomeArquivo;
    delete dadosLimpos.corDescricao;
    
    // Cores serão salvas separadamente
    delete dadosLimpos.cores;
    
    // Reporte 02/09 (item A10b, decisão P7 "Considere o 0"): NÃO reescrever IPI/Peso/Otimização.
    // O clamp "< 1 → 1" que morava aqui gravava valores que o usuário não digitou e sujava o Log
    // de Atualizações ("IPI 0 → 1" no print do componente 277989). O comentário que o justificava
    // ("backend exige >= 1") está desatualizado desde 11/08, quando o Range passou a aceitar 0.
    if (dadosLimpos.ipi == null) dadosLimpos.ipi = 0;
    if (dadosLimpos.peso == null) dadosLimpos.peso = 0;
    if (dadosLimpos.otimizacaoBarra == null) dadosLimpos.otimizacaoBarra = 0;


    // IdGrupo, IdLinha, IdReferencia - remover se for 0, undefined, null ou negativo
    // O frontend já valida que esses campos são obrigatórios, mas garantimos aqui também
    if (!dadosLimpos.idGrupo || dadosLimpos.idGrupo < 1) {
      delete dadosLimpos.idGrupo;
    }
    if (!dadosLimpos.idLinha || dadosLimpos.idLinha < 1) {
      delete dadosLimpos.idLinha;
    }
    if (!dadosLimpos.idReferencia || dadosLimpos.idReferencia < 1) {
      delete dadosLimpos.idReferencia;
    }
    
    // IdUnidadeItem e IdUnidadeItemEmbalagem
    if (!dadosLimpos.idUnidadeItem || dadosLimpos.idUnidadeItem < 1) {
      delete dadosLimpos.idUnidadeItem;
    }
    
    // Mapear idUnidadeEmbalagem para idUnidadeItemEmbalagem
    if (dadosLimpos.idUnidadeEmbalagem && dadosLimpos.idUnidadeEmbalagem > 0) {
      dadosLimpos.idUnidadeItemEmbalagem = dadosLimpos.idUnidadeEmbalagem;
    }
    if (!dadosLimpos.idUnidadeItemEmbalagem || dadosLimpos.idUnidadeItemEmbalagem < 1) {
      delete dadosLimpos.idUnidadeItemEmbalagem;
    }
    // Remover idUnidadeEmbalagem após mapear
    delete dadosLimpos.idUnidadeEmbalagem;
    
    // Garantir QuantidadeEmbalagem (obrigatório no banco)
    // Mapear quantidadeEmbalagem (camelCase) para QuantidadeEmbalagem (PascalCase) para o backend
    if (!dadosLimpos.quantidadeEmbalagem || dadosLimpos.quantidadeEmbalagem < 0.01) {
      dadosLimpos.quantidadeEmbalagem = 1;
    }
    // Garantir que o nome do campo esteja correto para o backend
    if (dadosLimpos.quantidadeEmbalagem !== undefined) {
      dadosLimpos.QuantidadeEmbalagem = dadosLimpos.quantidadeEmbalagem;
    }
    
    // Tratar IdCor baseado na aplicação
    // Se aplicacao === 'colorido', não enviar IdCor (cores são salvas separadamente)
    // Se aplicacao === 'cor_unica', IdCor é obrigatório e deve ser >= 1
    if (dadosLimpos.aplicacao === 'colorido') {
      // Remover IdCor completamente quando for colorido
      delete dadosLimpos.idCor;
    } else if (dadosLimpos.aplicacao === 'cor_unica') {
      // Garantir que IdCor seja válido (>= 1) quando for cor_unica
      if (!dadosLimpos.idCor || dadosLimpos.idCor < 1) {
        // Se IdCor não for válido, remover para que o backend retorne erro de validação claro
        delete dadosLimpos.idCor;
      }
    } else {
      // Se aplicacao não estiver definida ou for outro valor, remover IdCor para evitar erro
      delete dadosLimpos.idCor;
    }
    
    const { data } = await api[method](endpoint, dadosLimpos);
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao salvar componente");
    return null;
  }
};

export const excluirComponente = async (idComponente: number): Promise<boolean> => {
  try {
    await api.delete(`/ComponenteN1/Excluir/${idComponente}`);
    return true;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao excluir componente");
    return false;
  }
};

export const duplicarComponente = async (idComponente: number): Promise<{ id: number; sucesso: boolean; mensagem: string } | null> => {
  try {
    const { data } = await api.post(`/ComponenteN1/Duplicar/${idComponente}`);
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao duplicar componente");
    return null;
  }
};

export const validarCodigoUnico = async (codigo: string, idExcluir?: number): Promise<boolean> => {
  try {
    const params: any = { codigo };
    if (idExcluir) params.idExcluir = idExcluir;
    
    const { data } = await api.get('/ComponenteN1/ValidarCodigoUnico', { params });
    return data.disponivel;
  } catch (error: any) {
    console.error("Erro ao validar código:", error);
    return false;
  }
};

// ===== Listas de Referência =====
// Usa o controller ReferenciaDadosController que não exige permissão "Configuracao"

export const listarReferencias = async (idTipoTela?: number): Promise<ISelectOption[]> => {
  try {
    const params: any = {};
    if (idTipoTela) params.idTipoTela = idTipoTela;
    const { data } = await api.get('/ReferenciaDados/Referencias', { params });
    return (data || []).map((item: any) => ({
      id: item.idReferencia || item.id,
      nome: item.descricao || item.nome,
      descricao: item.descricao,
    }));
  } catch (error: any) {
    console.error("Erro ao listar referências:", error);
    return [];
  }
};

export const listarLinhas = async (idReferencia?: number): Promise<ISelectOption[]> => {
  try {
    const params: any = {};
    if (idReferencia) params.idReferencia = idReferencia;
    
    const { data } = await api.get('/ReferenciaDados/Linhas', { params });
    return (data || []).map((item: any) => ({
      id: item.idLinha || item.id,
      nome: item.descricao || item.nome,
      descricao: item.descricao,
    }));
  } catch (error: any) {
    console.error("Erro ao listar linhas:", error);
    return [];
  }
};

export const listarGrupos = async (): Promise<ISelectOption[]> => {
  try {
    // FASE 05 — 4ª validação/Frente 9 2026-07-30: recorta pelos grupos com a flag
    // Componente marcada no Cadastro de Grupos (filtro aplicado no backend).
    const { data } = await api.get('/ReferenciaDados/Grupos', { params: { tipoMaterial: 'componentes' } });
    return (data || []).map((item: any) => ({
      id: item.idGrupo || item.id,
      nome: item.descricao || item.nome,
      descricao: item.descricao,
    }));
  } catch (error: any) {
    console.error("Erro ao listar grupos:", error);
    return [];
  }
};

export const listarCores = async (): Promise<ISelectOption[]> => {
  try {
    // FASE 05 — 4ª validação/Frente 9 2026-07-30: recorta pelas cores com a flag
    // Componente marcada no cadastro de cores (filtro aplicado no backend).
    const { data } = await api.get('/ReferenciaDados/Cores', { params: { tipoMaterial: 'componentes' } });
    return (data || []).map((item: any) => ({
      id: item.idCor || item.id,
      nome: item.descricao || item.nome,
      descricao: item.descricao,
    }));
  } catch (error: any) {
    console.error("Erro ao listar cores:", error);
    return [];
  }
};

export const listarUnidades = async (): Promise<ISelectOption[]> => {
  try {
    const { data } = await api.get('/ReferenciaDados/Unidades');
    return (data || []).map((item: any) => ({
      id: item.idUnidade || item.id,
      nome: item.descricao || item.nome,
      descricao: item.descricao,
    }));
  } catch (error: any) {
    console.error("Erro ao listar unidades:", error);
    return [];
  }
};

// ===== Gerenciamento de Cores =====

export const obterCoresComponente = async (idComponente: number): Promise<IComponenteCor[]> => {
  try {
    const { data } = await api.get(`/ComponenteN1/${idComponente}/Cores`);
    return data.items || [];
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao obter cores do componente");
    return [];
  }
};

export const salvarCoresComponente = async (idComponente: number, cores: IComponenteCor[]): Promise<boolean> => {
  try {
    await api.post(`/ComponenteN1/${idComponente}/Cores`, cores);
    return true;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao salvar cores do componente");
    return false;
  }
};

// ===== Gerenciamento de Mídias =====

// Plano 21/09/2026 (Frente 3): as escritas de mídia devolvem também o `id` do registro gravado —
// com o produto Disponível, a API grava na cópia de trabalho e a tela precisa seguir para ela.
export const uploadImagem = async (idComponente: number, arquivo: File, nomeArquivo?: string): Promise<RetornoMidia | null> => {
  try {
    const formData = new FormData();
    formData.append('arquivo', arquivo);
    // Ajustes 25/08 (plano de mídias, Etapa A): o servidor grava com este nome — sem ele,
    // o arquivo era renomeado para um GUID e perdia o vínculo com os produtos.
    if (nomeArquivo) formData.append('nomeArquivo', nomeArquivo);
    
    const { data } = await api.post(`/ComponenteN1/${idComponente}/Midias/UploadImagem`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return data?.url ? { url: data.url, id: data.id ?? idComponente } : null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao fazer upload da imagem");
    return null;
  }
};

// FASE 05 — 2ª validação/PO 2026-07-24 (D2): upload de ícone (.bmp) e ficha técnica (.pdf/.jpg).
export const uploadIcone = async (idComponente: number, arquivo: File, nomeArquivo?: string): Promise<RetornoMidia | null> => {
  try {
    const formData = new FormData();
    formData.append('arquivo', arquivo);
    // Ajustes 25/08 (plano de mídias, Etapa A): o servidor grava com este nome — sem ele,
    // o arquivo era renomeado para um GUID e perdia o vínculo com os produtos.
    if (nomeArquivo) formData.append('nomeArquivo', nomeArquivo);
    const { data } = await api.post(`/ComponenteN1/${idComponente}/Midias/UploadIcone`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data?.url ? { url: data.url, id: data.id ?? idComponente } : null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao fazer upload do ícone");
    return null;
  }
};

export const uploadFichaTecnica = async (idComponente: number, arquivo: File, nomeArquivo?: string): Promise<RetornoMidia | null> => {
  try {
    const formData = new FormData();
    formData.append('arquivo', arquivo);
    // Ajustes 25/08 (plano de mídias, Etapa A): o servidor grava com este nome — sem ele,
    // o arquivo era renomeado para um GUID e perdia o vínculo com os produtos.
    if (nomeArquivo) formData.append('nomeArquivo', nomeArquivo);
    const { data } = await api.post(`/ComponenteN1/${idComponente}/Midias/UploadFichaTecnica`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data?.url ? { url: data.url, id: data.id ?? idComponente } : null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao fazer upload da ficha técnica");
    return null;
  }
};

export const removerImagem = async (idComponente: number): Promise<RetornoRemocaoMidia | null> => {
  try {
    const { data } = await api.delete(`/ComponenteN1/${idComponente}/Midias/RemoverImagem`);
    return { sucesso: true, id: data?.id ?? idComponente };
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao remover imagem");
    return null;
  }
};

// ===== Histórico =====

export const obterHistoricoComponente = async (idComponente: number): Promise<any[]> => {
  try {
    const { data } = await api.get(`/ComponenteN1/${idComponente}/Historico`);
    return data.items || [];
  } catch (error: any) {
    console.error("Erro ao obter histórico:", error);
    return [];
  }
};

// ===== Definir apenas o NOME do arquivo (sem upload) =====

/**
 * Reporte de 31/08 (item 6): grava só o nome informado, sem enviar arquivo — é assim que o
 * produto assume uma mídia que já foi carregada por outro produto da empresa (F05A-P3b).
 * Devolve `arquivoExiste` para a tela avisar quando o nome ainda não tem arquivo no acervo.
 */
export const definirNomeMidiaComponente = async (
  idComponente: number,
  tipoMidia: "icone" | "figura" | "fichaTecnica",
  nomeArquivo: string
): Promise<RetornoNomeMidia | null> => {
  try {
    const { data } = await api.put(`/ComponenteN1/${idComponente}/Midias/DefinirNome`, {
      tipoMidia,
      nomeArquivo,
    });
    return data ?? null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao gravar o nome do arquivo");
    return null;
  }
};
